import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import api from "@/api/client";
import ProductCard from "@/components/ProductCard";

const CATS = [
  { key: "all", label: "All items" },
  { key: "polymers", label: "Polymers" },
  { key: "chemicals", label: "Chemicals" },
  { key: "cpw_oils", label: "CPW Oils" },
];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("category") || "all";
  const [category, setCategory] = useState(initialCat);
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (category !== "all") q.category = category;
    if (search) q.search = search;
    api.get("/products", { params: q }).then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, [category, search]);

  useEffect(() => {
    if (category === "all") params.delete("category");
    else params.set("category", category);
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  return (
    <div className="min-h-screen bg-zinc-100">
      <div className="border-b border-zinc-900 bg-white">
        <div className="px-6 lg:px-10 py-8">
          <div className="tag">CATALOG</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight mt-1">
            Live raw material inventory
          </h1>
          <p className="text-zinc-500 mt-2 max-w-2xl">
            Every SKU shows real-time stock, price and MOQ. Trade with confidence.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[260px] flex items-center border-2 border-zinc-900 bg-white">
              <Search className="w-4 h-4 mx-3 text-zinc-500" />
              <input
                data-testid="catalog-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search HDPE, Caustic Soda, Palm Oil..."
                className="flex-1 py-3 outline-none font-medium"
              />
            </div>
            <div className="flex items-center gap-2 border-2 border-zinc-900 px-3 py-3">
              <SlidersHorizontal className="w-4 h-4" />
              <span className="tag">FILTERS</span>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button
                key={c.key}
                data-testid={`category-filter-${c.key}`}
                onClick={() => setCategory(c.key)}
                className={`px-4 py-2 font-mono-data text-xs tracking-widest border-2 border-zinc-900 ${
                  category === c.key
                    ? "bg-zinc-900 text-white"
                    : "bg-white hover:bg-zinc-100"
                }`}
              >
                {c.label.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-10 py-10">
        <div className="flex items-center justify-between mb-6">
          <div className="tag" data-testid="catalog-result-count">
            {loading ? "LOADING..." : `${products.length} SKUS FOUND`}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-[4/3] bg-zinc-200 animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-zinc-400 p-12 text-center">
            <div className="font-display font-black text-2xl">Nothing matched</div>
            <p className="text-zinc-500 mt-2">Try a different filter or clear the search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
